import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';

import {
    fetchGames as apiFetchGames,
    fetchLiveGamesNewLobby as apiFetchLiveGamesNewLobby,
    fetchGamesSettings as apiFetchGamesSettings,
    fetchNewGames as apiFetchNewGames,
} from 'http';
import {
    addGameToRecent as apiAddGameToRecent,
    fetchFavoriteGames as apiFetchFavoriteGames,
    fetchRecentGames as apiFetchRecentGames,
    saveFavoriteGames as apiSaveFavoriteGames,
    toggleFavoriteGame as apiToggleFavoriteGame,
} from 'localapi';

export const fetchGames = createAsyncThunk(
    'games/fetchGames',
    async (props, { getState, rejectWithValue }) => {
        try {
            props = props || {};
            const {
                includeLiveGamesFromNewLobby = false,
                skipAllGamesExceptLive = false,
                liveGamesProvider,
                liveGamesHallId,
            } = props;
            const { auth } = getState();
            let games;
            if (!skipAllGamesExceptLive) {
                games = await apiFetchGames(auth.hallId);
            } else {
                games = [];
            }
            if (includeLiveGamesFromNewLobby) {
                const liveTables = await apiFetchLiveGamesNewLobby({
                    hallID: liveGamesHallId,
                    gameProvider: liveGamesProvider,
                });
                games = [...games, ...liveTables.map(table => ({
                    "icon": table.icon,
                    "id": table.stargameId,
                    "family": "Other",
                    "name": table.name,
                    "category": "other",
                    "position": 0,
                    "popularity": 0,
                    "vendor": "other",
                    "isVendorProvided": true,
                    "isLive": true,
                }))];
            }
            return games;
        } catch (err) {
            return rejectWithValue(err.message || `error: ${err}`);
        }
    }
);

export const fetchGameSettings = createAsyncThunk(
    'games/fetchGameSettings',
    async (_, { getState, rejectWithValue }) => {
        try {
            const { auth } = getState();
            return await apiFetchGamesSettings(auth.uid);
        } catch (err) {
            return rejectWithValue(err.message || `error: ${err}`);
        }
    }
);

export const fetchNewGames = createAsyncThunk(
    'games/fetchNewGames',
    async (_, { rejectWithValue }) => {
        try {
            return await apiFetchNewGames();
        } catch (err) {
            return rejectWithValue(err.message || `error: ${err}`);
        }
    }
);

export const fetchFavoriteGames = createAsyncThunk(
    'games/fetchFavoriteGames',
    async (_, { getState, rejectWithValue }) => {
        try {
            const { auth } = getState();
            return await apiFetchFavoriteGames(auth.uid);
        } catch (err) {
            return rejectWithValue(err.message || `error: ${err}`);
        }
    }
);

export const toggleFavoriteGame = createAsyncThunk(
    'games/toggleFavoriteGame',
    async (gameId, { getState, rejectWithValue }) => {
        try {
            const { auth } = getState();
            return await apiToggleFavoriteGame(auth.uid, gameId);
        } catch (err) {
            return rejectWithValue(err.message || `error: ${err}`);
        }
    }
);

export const saveFavoriteGames = createAsyncThunk(
    'games/saveFavoriteGames',
    async (gameIds, { getState, rejectWithValue }) => {
        try {
            const { auth } = getState();
            return await apiSaveFavoriteGames(auth.uid, gameIds);
        } catch (err) {
            return rejectWithValue(err.message || `error: ${err}`);
        }
    }
);

export const addGameToRecent = createAsyncThunk(
    'games/addGameToRecent',
    async (gameId, { getState, rejectWithValue }) => {
        try {
            const { auth } = getState();
            return await apiAddGameToRecent(auth.uid, gameId);
        } catch (err) {
            return rejectWithValue(err.message || `error: ${err}`);
        }
    }
);

export const fetchRecentGames = createAsyncThunk(
    'games/fetchRecentGames',
    async (_, { getState, rejectWithValue }) => {
        try {
            const { auth } = getState();
            return await apiFetchRecentGames(auth.uid);
        } catch (err) {
            return rejectWithValue(err.message || `error: ${err}`);
        }
    }
);

const bonusGameIds = {
    '158': 401,
    '726': 402,
    '577': 403,
    '711': 404,
};

const pragmaticDemoDisabled = {
    '1301': {},
    '2494': {},
    '2495': {},
    '2496': {},
    '2497': {},
    '2498': {},
    '2499': {},
    '2500': {},
    '2501': {},
    '2502': {},
    '2503': {},
};

const luckyBonusGameId = 949;

const isAsia = window.location.host.includes('asia.games') || window.location.host.includes('row.solutions');

export const gamesSlice = createSlice({
    name: 'games',
    initialState: {
        gameEntities: {
            [luckyBonusGameId]: {
                id: luckyBonusGameId,
                name: 'Lucky Bonus',
            }
        },
        gameIds: [],
        whiteList: null,
        favoriteGameIds: [],
        favoriteGameIdsMap: {},
        recentGameIds: [],
        gameSettings: {},
        newGameIds: [],
        gameListSearch: "",
        gameListSortDirection: "DESC",
        gameListSortKey: "position",
        gameListCursor: 0,
        isGameListFetching: false,
        isGameListSuccess: false,
        isGameListError: false,
    },
    reducers: {
        setGameListSearch(state, action) {
            state.gameListSearch = action.payload;
        },
        setGameListSort(state, action) {
            state.gameListSortKey = action.payload;
        },
        setGameListCursor(state, action) {
            state.gameListCursor = action.payload;
        },
        setWhiteList(state, action) {
            state.whiteList = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(fetchGames.fulfilled, (state, { payload }) => {
            const entities = (payload || []).reduce((acc, curr) => {
                if (curr.vendor === 'pragmatic' && pragmaticDemoDisabled[curr.id] === undefined) {
                    curr.hasDemo = true;
                }
                acc[curr.id] = curr;
                return acc;
            }, {});

            state.gameIds = isAsia ?
                Object.keys(entities).filter(id => Number(id) < 4000 && Number(id) !== luckyBonusGameId) :
                Object.keys(entities).filter(id => Number(id) !== luckyBonusGameId);

            Object.keys(bonusGameIds).forEach(id => {
                const bonusId = bonusGameIds[id];
                if (entities[id] !== undefined) {
                    entities[bonusId] = {
                        ...entities[id],
                        id: bonusId,
                        hidden: true
                    };
                }
            });

            state.gameEntities = { ...entities, [luckyBonusGameId]: { id: luckyBonusGameId, name: 'Lucky Bonus' } };
            state.isGameListFetching = false;
            state.isGameListSuccess = true;
            state.isGameListError = false;
            return state;
        });
        builder.addCase(fetchGames.rejected, (state, { payload }) => {
            console.warn('fetchGames rejected:', payload);
            state.isGameListFetching = false;
            state.isGameListSuccess = false;
            state.isGameListError = true;
        });
        builder.addCase(fetchGames.pending, (state) => {
            state.isGameListFetching = true;
        });
        builder.addCase(fetchGameSettings.fulfilled, (state, { payload }) => {
            state.gameSettings = payload;
            return state;
        });
        builder.addCase(fetchNewGames.fulfilled, (state, { payload }) => {
            if (isAsia) {
                state.newGameIds =
                    payload
                        .filter(a => state.gameEntities[a.id] && Number(a.id) < 4000 && Number(a.id) !== luckyBonusGameId)
                        .sort((a, b) => b.htmlposition - a.htmlposition)
                        .map(a => a.id);
            } else {
                state.newGameIds =
                    payload
                        .filter(a => state.gameEntities[a.id] && Number(a.id) !== luckyBonusGameId)
                        .sort((a, b) => b.htmlposition - a.htmlposition)
                        .map(a => a.id);
            }
            return state;
        });
        builder.addMatcher(isAnyOf(fetchFavoriteGames.fulfilled, saveFavoriteGames.fulfilled, toggleFavoriteGame.fulfilled), (state, { payload }) => {
            state.favoriteGameIds = payload.filter(id => state.gameEntities[id] && id !== luckyBonusGameId);
            state.favoriteGameIdsMap = state.favoriteGameIds
                .reduce((acc, curr) => {
                    acc[curr] = 1;
                    return acc;
                }, {});
            return state;
        });
        builder.addMatcher(isAnyOf(fetchRecentGames.fulfilled, addGameToRecent.fulfilled), (state, { payload }) => {
            state.recentGameIds = payload.filter(id => state.gameEntities[id] && Number(id) !== luckyBonusGameId);
            return state;
        });
    },
});

export const gamesSelector = (state) => state.games;
export const {
    setGameListCursor,
    setGameListSearch,
    setGameListSort,
    setWhiteList,
} = gamesSlice.actions;
export default gamesSlice.reducer;